import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaChevronDown, FaMapMarkerAlt } from 'react-icons/fa';
import { getPropertyTypes, getMunicipiosYBarrios } from '../services/api';

const SearchBar = () => {
  const navigate = useNavigate();
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPropertyType, setSelectedPropertyType] = useState('');
  const [isPropertyDropdownOpen, setIsPropertyDropdownOpen] = useState(false);
  const [selectedServiceType, setSelectedServiceType] = useState('');
  const [isServiceDropdownOpen, setIsServiceDropdownOpen] = useState(false);
  const [locationInput, setLocationInput] = useState('');
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);

  // Añadir estilos CSS para el dropdown personalizado
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* Dropdown personalizado */
      .custom-dropdown {
        position: relative;
      }
      
      .dropdown-list {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: #0D0D0D;
        border: 2px solid #D4AF37;
        border-top: none;
        border-radius: 0 0 8px 8px;
        max-height: 200px;
        overflow-y: auto;
        z-index: 50;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
      }
      
      .dropdown-option {
        padding: 12px 16px;
        color: #FFFFFF;
        cursor: pointer;
        border-bottom: 1px solid #333;
        transition: all 0.2s ease;
        font-size: 14px;
      }
      
      .dropdown-option:hover {
        background-color: #D4AF37;
        color: #0D0D0D;
      }
      
      .dropdown-option:last-child {
        border-bottom: none;
        border-radius: 0 0 6px 6px;
      }
      
      /* Scrollbar personalizado */
      .dropdown-list::-webkit-scrollbar {
        width: 8px;
      }
      
      .dropdown-list::-webkit-scrollbar-track {
        background: #0D0D0D;
        border-radius: 4px;
      }
      
      .dropdown-list::-webkit-scrollbar-thumb {
        background: #D4AF37;
        border-radius: 4px;
        border: 1px solid #0D0D0D;
      }
      
      .dropdown-list::-webkit-scrollbar-thumb:hover {
        background: #B8941F;
      }
      
      /* Para Firefox */
      .dropdown-list {
        scrollbar-width: thin;
        scrollbar-color: #D4AF37 #0D0D0D;
      }
      
      /* Responsivo */
      @media (max-width: 768px) {
        .dropdown-list {
          max-height: 150px;
        }
        
        .dropdown-option {
          padding: 10px 12px;
          font-size: 13px;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);
        const [types, locationsData] = await Promise.all([
          getPropertyTypes(),
          getMunicipiosYBarrios()
        ]);
        setPropertyTypes(types);
        setLocations(locationsData);
      } catch (error) {
        console.error('Error fetching initial data:', error.message);
        // Mostrar mensaje de error al usuario
        alert(`Error al cargar los datos: ${error.message}. Por favor, revisa la configuración de la API.`);
        // Set empty arrays if API fails
        setPropertyTypes([]);
        setLocations([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  const filteredLocations = useMemo(() => {
    if (!locationInput) {
      return [];
    }
    const searchLower = locationInput.toLowerCase();
    const results = [];

    locations.forEach(municipio => {
      // Si el municipio coincide
      if (municipio.municipio.toLowerCase().includes(searchLower)) {
        results.push({
          id: `mun-${municipio.codmunicipio}`,
          label: municipio.municipio,
          type: 'municipio',
          ...municipio
        });
      }
      // Si algún barrio coincide
      municipio.barrios.forEach(barrio => {
        if (barrio.barrio.toLowerCase().includes(searchLower)) {
          results.push({
            id: `bar-${barrio.codbarrio}`,
            label: `${barrio.barrio} - ${municipio.municipio}`,
            type: 'barrio',
            ...barrio,
            municipioNombre: municipio.municipio
          });
        }
      });
    });
    return results;
  }, [locationInput, locations]);

  const handleLocationSelect = (location) => {
    setLocationInput(location.label);
    setIsLocationDropdownOpen(false);
  };

  const handleSearch = () => {
    // Construir parámetros de búsqueda
    const searchParams = new URLSearchParams();
    
    if (selectedServiceType) {
      searchParams.append('destinacion', selectedServiceType);
    }
    
    if (selectedPropertyType) {
      searchParams.append('clase', selectedPropertyType);
    }
    
    // Procesar ubicación (barrio - municipio)
    if (locationInput) {
      const locationParts = locationInput.split(' - ');
      if (locationParts.length === 2) {
        searchParams.append('barrio', locationParts[0].trim());
        searchParams.append('municipio', locationParts[1].trim());
      } else {
        // Si no tiene el formato esperado, buscar en municipios
        searchParams.append('municipio', locationInput);
      }
    }
    
    // Navegar a la página de propiedades con los parámetros
    navigate(`/properties?${searchParams.toString()}`);
  };

  const handleLocationInputChange = (e) => {
    setLocationInput(e.target.value);
    if (e.target.value) {
      setIsLocationDropdownOpen(true);
    } else {
      setIsLocationDropdownOpen(false);
    }
  };

  const handlePropertyTypeSelect = (type) => {
    setSelectedPropertyType(type);
    setIsPropertyDropdownOpen(false);
  };

  const togglePropertyDropdown = () => {
    setIsPropertyDropdownOpen(!isPropertyDropdownOpen);
    setIsServiceDropdownOpen(false);
    setIsLocationDropdownOpen(false);
  };

  const handleServiceTypeSelect = (service) => {
    setSelectedServiceType(service);
    setIsServiceDropdownOpen(false);
  };

  const toggleServiceDropdown = () => {
    setIsServiceDropdownOpen(!isServiceDropdownOpen);
    setIsPropertyDropdownOpen(false);
    setIsLocationDropdownOpen(false);
  };

  // Cerrar dropdowns cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.custom-dropdown')) {
        setIsPropertyDropdownOpen(false);
        setIsServiceDropdownOpen(false);
        setIsLocationDropdownOpen(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className="bg-brand-black/90 p-6 rounded-lg shadow-2xl w-full backdrop-blur-sm">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center">
        {/* Dropdown Tipo de Servicio */}
        <div className="custom-dropdown relative md:col-span-1">
          <button
            type="button"
            onClick={toggleServiceDropdown}
            className="w-full p-3 border-2 border-brand-gold rounded-lg bg-brand-black text-brand-white hover:border-brand-gold/80 focus:border-brand-gold focus:ring-1 focus:ring-brand-gold transition-colors h-12 flex items-center justify-between text-left"
          >
            <span className={selectedServiceType ? 'text-brand-white' : 'text-brand-gold/70'}>
              {selectedServiceType || 'Tipo de servicio'}
            </span>
            <FaChevronDown 
              className={`text-brand-gold transition-transform duration-200 ${
                isServiceDropdownOpen ? 'rotate-180' : ''
              }`} 
              size={14}
            />
          </button>
          
          {isServiceDropdownOpen && (
            <div className="dropdown-list">
              <div
                onClick={() => handleServiceTypeSelect('Venta')}
                className="dropdown-option"
              >
                Venta
              </div>
              <div
                onClick={() => handleServiceTypeSelect('Arriendo')}
                className="dropdown-option"
              >
                Arriendo
              </div>
            </div>
          )}
        </div>

        {/* Dropdown Tipo de Propiedad */}
        <div className="custom-dropdown relative md:col-span-1">
          <button
            type="button"
            onClick={togglePropertyDropdown}
            disabled={isLoading}
            className="w-full p-3 border-2 border-brand-gold rounded-lg bg-brand-black text-brand-white hover:border-brand-gold/80 focus:border-brand-gold focus:ring-1 focus:ring-brand-gold transition-colors h-12 flex items-center justify-between text-left"
          >
            <span className={selectedPropertyType ? 'text-brand-white' : 'text-brand-gold/70'}>
              {isLoading 
                ? 'Cargando tipos...' 
                : selectedPropertyType || 'Tipo de propiedad'
              }
            </span>
            <FaChevronDown 
              className={`text-brand-gold transition-transform duration-200 ${
                isPropertyDropdownOpen ? 'rotate-180' : ''
              }`} 
              size={14}
            />
          </button>
          
          {isPropertyDropdownOpen && !isLoading && (
            <div className="dropdown-list">
              {propertyTypes.length > 0 ? (
                propertyTypes.map(type => (
                  <div
                    key={type}
                    onClick={() => handlePropertyTypeSelect(type)}
                    className="dropdown-option"
                  >
                    {type}
                  </div>
                ))
              ) : (
                <div className="dropdown-option text-brand-gold/50">
                  No hay tipos de propiedad disponibles
                </div>
              )}
            </div>
          )}
        </div>

        {/* Input de Ubicación */}
        <div className="custom-dropdown relative md:col-span-2">
          <div className="relative">
            <FaMapMarkerAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-gold" />
            <input
              type="text"
              value={locationInput}
              onChange={handleLocationInputChange}
              onFocus={() => locationInput && setIsLocationDropdownOpen(true)}
              placeholder="Busca por barrio o municipio"
              className="w-full p-3 pl-10 border-2 border-brand-gold rounded-lg bg-brand-black text-brand-white hover:border-brand-gold/80 focus:border-brand-gold focus:ring-1 focus:ring-brand-gold transition-colors h-12"
            />
          </div>
          {isLocationDropdownOpen && filteredLocations.length > 0 && (
            <div className="dropdown-list">
              {filteredLocations.map(loc => (
                <div
                  key={loc.id}
                  onClick={() => handleLocationSelect(loc)}
                  className="dropdown-option"
                >
                  {loc.label}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Botón de Búsqueda */}
        <button 
          onClick={handleSearch}
          className="bg-brand-gold text-brand-black p-3 rounded-lg flex items-center justify-center hover:bg-brand-white hover:text-brand-black transition-all duration-300 shadow-md hover:shadow-xl border-2 border-brand-gold h-12 md:col-span-1"
        >
          <FaSearch size={20} />
        </button>
      </div>
    </div>
  );
};

export default SearchBar;
